import { commonClient, type NearbyShow } from '@bip/api-client';
import type { NearbyContext, NearbyFetchResult } from '../nearby.types';

function ddmmyyyyToISO(date: string | undefined | null): string {
  if (!date) return '';
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(date).trim());
  if (!m) return '';
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

function buildResult(shows: NearbyShow[], total: number): NearbyFetchResult<NearbyShow> {
  // TEMP DEBUG — check in the browser devtools console whether the
  // backend response actually carries `IsActive` on each nearby show.
  // Remove once confirmed on the test server.
  console.log('[NearbyShows] raw shows from API:', shows.map((s) => ({ name: s.name, IsActive: s.IsActive })));

  if (!shows.length) return { items: [], total: 0 };

  const first = shows[0];
  const exploreParams = new URLSearchParams();
  if (first?.venueCityId) exploreParams.set('cityId', String(first.venueCityId));
  const iso = ddmmyyyyToISO(first?.dateOfEvent);
  if (iso) exploreParams.set('hotelBookingDate', iso);
  const exploreHref = exploreParams.toString() ? `/shows?${exploreParams.toString()}` : '/shows';

  return { items: shows, total, exploreHref };
}

/**
 * Nearby-shows fetcher. Goes through the common-service cross-sell hub
 * (`GET /bip-api/common/nearby/shows`), which internally picks between
 * two upstreams:
 *   • when `eventId` is passed → sports-events-service's event-scoped
 *     `/events/{id}/nearby-shows` (same city, same date as that event,
 *     ranked by distance from the sports venue — matches the original
 *     sports-page `NearbyShows` widget behaviour).
 *   • otherwise → sports-events-service's geo-radius
 *     `/events/nearby-shows-geo` (used by restaurants + hotels pages).
 * That branching used to live here, split across two clients; it now
 * lives inside the hub so the frontend just forwards the whole param
 * bag.
 */
export async function fetchNearbyShows(ctx: NearbyContext): Promise<NearbyFetchResult<NearbyShow>> {
  const res = await commonClient.getNearbyShows({
    latitude: ctx.latitude,
    longitude: ctx.longitude,
    distance: 50,
    // Default fetch limit is 10 (bumped from 4) — matches every other
    // nearby adapter. The strip's Explore button surfaces whenever
    // `items.length > 4`, so the extra rows give the "there's more"
    // signal room to fire.
    limit: 10,
    ...(ctx.dateFrom ? { dateFrom: ctx.dateFrom } : {}),
    ...(ctx.userCurrency ? { userCurrency: ctx.userCurrency } : {}),
    ...(ctx.eventId != null ? { eventId: ctx.eventId } : {}),
  });

  return buildResult(res.shows ?? [], res.total ?? res.shows?.length ?? 0);
}
